export type EntityId = string;

export type EntityWithId = {
    id?: EntityId | null;
};

export const appendEntity = <T>(items: readonly T[], item: T): T[] => [...items, item];

export const prependEntity = <T>(items: readonly T[], item: T): T[] => [item, ...items];

export const removeEntityById = <T extends EntityWithId>(items: readonly T[], id: EntityId): T[] =>
    items.filter((item) => item.id !== id);

export const replaceEntityById = <T extends EntityWithId>(items: readonly T[], id: EntityId, replacement: T): T[] =>
    items.map((item) => (item.id === id ? replacement : item));

export const replaceEntityAtIndex = <T>(items: readonly T[], index: number, replacement: T): T[] =>
    items.map((item, itemIndex) => (itemIndex === index ? replacement : item));

export const mergeEntityById = <T extends EntityWithId>(items: readonly T[], id: EntityId, updates: Partial<T>): T[] =>
    items.map((item) => (item.id === id ? ({ ...item, ...updates } as T) : item));

export const mergeEntity = <T>(item: T, updates: Partial<T>): T => ({ ...item, ...updates });

/**
 * Inserts a complete entity or replaces the existing entity with the same id.
 * Partial patches must be merged first (for example with `mergeEntityById` or
 * `reduceEntityDelta`) so omitted fields are not discarded.
 */
export const upsertEntityById = <T extends EntityWithId>(
    items: readonly T[],
    item: T,
    options?: { prepend?: boolean },
): T[] => {
    if (!item.id) {
        return options?.prepend ? prependEntity(items, item) : appendEntity(items, item);
    }

    const existingIndex = items.findIndex((existing) => existing.id === item.id);

    if (existingIndex !== -1) {
        let didReplace = false;

        return items.reduce<T[]>((acc, existing) => {
            if (existing.id !== item.id) {
                acc.push(existing);

                return acc;
            }

            if (!didReplace) {
                acc.push(item);
                didReplace = true;
            }

            return acc;
        }, []);
    }

    return options?.prepend ? prependEntity(items, item) : appendEntity(items, item);
};

export const replaceOptimisticEntityById = <T extends EntityWithId>(
    items: readonly T[],
    optimisticId: EntityId,
    item: T,
): T[] => {
    if (!item.id) {
        return replaceEntityById(items, optimisticId, item);
    }

    const optimisticIndex = items.findIndex((existing) => existing.id === optimisticId);
    const canonicalIndex = items.findIndex((existing) => existing.id === item.id);
    const insertionIndex = optimisticIndex !== -1 ? optimisticIndex : canonicalIndex;

    if (insertionIndex === -1) {
        return appendEntity(items, item);
    }

    const filtered = items.filter((existing) => existing.id !== optimisticId && existing.id !== item.id);
    const next = [...filtered];

    next.splice(Math.min(insertionIndex, next.length), 0, item);

    return next;
};

const pendingSingleFlightRequests = new Map<string, Promise<unknown>>();

const stableSerialize = (value: unknown): string => {
    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value);
    }

    if (value instanceof Date) {
        return JSON.stringify(value.toISOString());
    }

    if (Array.isArray(value)) {
        return `[${value.map(stableSerialize).join(',')}]`;
    }

    return `{${Object.entries(value as Record<string, unknown>)
        .filter(([entryKey, entryValue]) => !['createdAt', 'updatedAt'].includes(entryKey) && entryValue !== undefined)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableSerialize(entryValue)}`)
        .join(',')}}`;
};

export const createSingleFlightKey = (scope: string, input: unknown): string => `${scope}:${stableSerialize(input)}`;

export const runSingleFlight = async <T>(key: string, action: () => Promise<T>): Promise<T> => {
    const pending = pendingSingleFlightRequests.get(key) as Promise<T> | undefined;

    if (pending) {
        return pending;
    }

    const promise = action().finally(() => {
        if (pendingSingleFlightRequests.get(key) === promise) {
            pendingSingleFlightRequests.delete(key);
        }
    });

    pendingSingleFlightRequests.set(key, promise);

    return promise;
};
